import VerifiedIcon from "@mui/icons-material/Verified";
import { Avatar, Stack, Tooltip, Typography } from "@mui/material";
import { SocialStatsRow } from "Components/Social/SocialStatsRow";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { ProfileActions } from "Pages/PublicProfile/ProfileActions";
import type { PublicProfileDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { getContextProfilePhotoUrl } from "Utils/Image/getProfilePhotoUrl";

const RESOURCES = Resources.Social.PublicProfile;

type Props = {
  profile: PublicProfileDto;
};

/**
 * Body of the public profile page: avatar + @handle always; a public profile adds the
 * display name (with the identity-verified badge when KYC passed) and bio, a private one
 * the "Private account" note.
 */
export const ProfileContent = ({ profile }: Props) => {
  const { t } = useResource();

  // The persona's own avatar. SSO-sourced pictures (a full URL stored instead of a blob path)
  // answer 204 on this endpoint, so the MUI Avatar falls back to the initial.
  const avatarUrl = getContextProfilePhotoUrl(
    profile.identityUserPublicID,
    profile.profilePicture,
  );

  return (
    <Stack gap={1.5} alignItems="center">
      <Avatar
        src={avatarUrl}
        sx={{ width: 96, height: 96, fontSize: 40, mt: { xs: 4, sm: 0 } }}
      >
        {(profile.displayName || profile.handle || "?")[0]?.toUpperCase()}
      </Avatar>
      {profile.isPublic && profile.displayName && (
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h5" fontWeight={500}>
            {profile.displayName}
          </Typography>
          {profile.isIdentityVerified && (
            <Tooltip
              title={t(
                Resources.Social.PublicPermissions.IdentityVerifiedTooltip,
              )}
            >
              <VerifiedIcon color="success" fontSize="small" />
            </Tooltip>
          )}
        </Stack>
      )}
      {profile.handle && (
        <Typography
          color="text.secondary"
          variant={profile.isPublic ? "body1" : "h5"}
        >
          @{profile.handle}
        </Typography>
      )}
      {profile.followerCount != null && (
        <SocialStatsRow
          stats={[
            // Only Customers follow, so only they can have mutual followers — business
            // profiles (merchants, hunters) show only the follow stats.
            ...(profile.accessRoleCode === AccessRoleCodes.Customer
              ? [
                  {
                    id: "mutual",
                    label: t(Resources.Social.Stats.Mutual),
                    count: profile.mutualFollowCount ?? 0,
                  },
                ]
              : []),
            {
              id: "followers",
              label: t(Resources.Social.Stats.Followers),
              count: profile.followerCount,
            },
            {
              id: "following",
              label: t(Resources.Social.Stats.Following),
              count: profile.followedCount ?? 0,
            },
          ]}
        />
      )}
      {profile.isPublic ? (
        profile.bio && <Typography>{profile.bio}</Typography>
      ) : (
        <Typography color="text.secondary">
          {t(RESOURCES.PrivateAccount)}
        </Typography>
      )}
      <ProfileActions profile={profile} />
    </Stack>
  );
};
